import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Avatar, Card } from "@/components/common";
import { useEvent, useEvents } from "@/context/AppContext";
import { api } from "@/lib/api";
import { num, fmtMin } from "@/lib/format";
import { useHidden, HideBtn, HiddenRow } from "@/components/HiddenProfiles";

const RELOAD = ["MESSAGES_ACCOUNT_LIST_UPDATED", "rt.resync"];
const byPaid = (a, b) => b.paid - a.paid || (a.username || "").toLowerCase().localeCompare((b.username || "").toLowerCase());
const waitMin = (at) => (at ? Math.max(0, Math.floor((Date.now() - new Date(at).getTime()) / 60000)) : null);

function PaidCard({ p, max, onHide }) {
  const zero = !p.paid;
  const m = waitMin(p.paid_oldest_at);
  return (
    <Link to={`/messages?a=${p.id}&f=unread&p=1`} className={`relative rounded-lg border border-[#232738] bg-[#0D0E14] p-3 transition-colors hover:border-amber-400/50 ${zero ? "opacity-50" : ""}`} data-testid={`paid-profile-${p.username}`}>
      {onHide && <HideBtn onClick={onHide} testid={`paid-profile-hide-${p.username}`} />}
      <div className="flex items-center gap-2.5">
        <Avatar name={p.creator_name || p.username} url={p.avatar_url} size={36} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold">{p.creator_name || "—"}</div>
          <div className="truncate text-xs text-slate-500">@{p.username}</div>
          <div className="whitespace-normal break-words text-[10px] text-slate-500" data-testid={`paid-profile-of-${p.username}`}>su {num(p.unread)} non lette
            {!zero && m != null && <> · <span className={`whitespace-nowrap ${m > 10 ? "font-bold text-red-400" : "text-slate-400"}`} data-testid={`paid-profile-wait-${p.username}`}>attende da {fmtMin(m)}</span></>}</div>
        </div>
        <div className={`num text-2xl font-extrabold ${zero ? "text-slate-500" : "text-amber-300"}`} data-testid={`paid-profile-count-${p.username}`}>{num(p.paid)}</div>
      </div>
      <div className="mt-2.5 h-1.5 w-full overflow-hidden rounded bg-white/5">
        <div className="h-full rounded bg-amber-400 transition-[width] duration-500" style={{ width: `${max ? Math.max(p.paid ? 4 : 0, (p.paid / max) * 100) : 0}%` }} />
      </div>
    </Link>
  );
}

// CHAT NON LETTE DEI PAGANTI PER PROFILO: same server counters as the yellow dots in Messages (UNREAD_COUNTERS), always "now" (no period).
export function PaidByProfile({ className = "mb-3" }) {
  const [d, setD] = useState(null);
  const [, tick] = useState(0);
  const t = useRef(null);
  const load = useCallback(() => api.get("/dashboard/unread-profiles", { timeout: 9000 }).then((r) => setD({ items: [...r.data.items].sort(byPaid) })).catch(() => {}), []);
  useEffect(() => { load(); const i = setInterval(() => tick((x) => x + 1), 30000); return () => { clearTimeout(t.current); clearInterval(i); }; }, [load]);
  useEvents(RELOAD, () => { clearTimeout(t.current); t.current = setTimeout(load, 600); });
  useEvent("UNREAD_COUNTERS", (c) => setD((p) => p && p.items.some((x) => x.id === c.account_id)
    ? { items: p.items.map((x) => (x.id === c.account_id ? { ...x, unread: c.unread, paid: Math.min(c.paid || 0, c.unread || 0), paid_oldest_at: c.paid_oldest_at } : x)).sort(byPaid) } : p));
  const hd = useHidden("paid");
  if (!d) return null;
  const vis = d.items.filter((x) => !hd.hidden.includes(x.id));
  const hid = d.items.filter((x) => hd.hidden.includes(x.id));
  const total = vis.reduce((s, x) => s + x.paid, 0);
  const hidTot = hid.reduce((s, x) => s + x.paid, 0);
  const max = Math.max(0, ...vis.map((x) => x.paid));
  return (
    <Card title="Chat non lette dei paganti per profilo" className={className} testid="paid-by-profile"
      actions={<span className="text-xs text-slate-400" data-testid="paid-by-profile-total">Totale <span className="num font-bold text-amber-300">{num(total)}</span>{hid.length > 0 && <> · {num(hidTot)} nei nascosti</>}</span>}>
      {!d.items.length ? <div className="py-4 text-center text-xs text-slate-500">Nessun profilo collegato</div> : (
        <>
          {!total && <div className="mb-2 text-center text-xs text-emerald-400" data-testid="paid-by-profile-none">Nessun pagante in attesa ✓</div>}
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-4">{vis.map((p) => <PaidCard key={p.id} p={p} max={max} onHide={hd.canEdit ? () => hd.set(p.id, true) : null} />)}</div>
          <HiddenRow items={hid} value={(p) => p.paid} canEdit={hd.canEdit} onShow={(id) => hd.set(id, false)} testid="paid-hidden" />
        </>
      )}
    </Card>
  );
}
